import type { Event, Pin, Resource, Shortlist, Status, Tag } from '__types__';
import type { ShortlistAction_normalized as ShortlistAction } from 'v4/entities/shortlist/shortlist.types';
import type { ResourceSearchQuery } from '../resource/resource.api';
import type { FieldNameTypeMapping } from '../../services/internal/MetastructureMappingService/MetastructureMappingService';
import type { GlobalPronouns } from '../system-settings/pronouns';

export const BLOCK_SORT_FIELDS = ['order', 'votes', 'name', 'added'] as const;
export type BlockSortField = (typeof BLOCK_SORT_FIELDS)[number];

/**
 * How the client sees the shortlist; set in Client view, shared with everyone. Always cards,
 * in the size kept in v1's `layout`.
 */
export interface ShortlistClientView {
  size: 'LARGE' | 'MEDIUM' | 'SMALL';
}

/** `board`: Edit, members only, the full data. `client`: Preview and the public page, the same for a member and a guest. */
export type BoardView = 'board' | 'client';

/**
 * One shortlist by its link, as one reader asks for it: the session, when there is one, makes
 * the reader a member; anyone else is a guest.
 */
export interface BoardRef {
  publicId: string;
  view: BoardView;
  /** Opens a password shortlist for a guest; a member needs none. */
  password: string | null;
}

/** The shortlist as its client sees it, the same for a member and a guest. */
export interface ClientShortlist {
  publicId: string;
  /** The title the client sees. */
  title: string | null;
  description: string | null;
  defaultFields: string[];
  cardFieldCount: number;
  clientView: ShortlistClientView;
  showAvailability: { startDate: string; endDate: string } | null;
  enableVoting: boolean;
  enableCommenting: boolean;
  publicFeedback: boolean;
  /**
   * Today's star: the filter clients open the shortlist with. The client view sends only its
   * filters on the card fields.
   */
  clientDefaultQuery: ResourceSearchQuery | null;
  blockCount: number;
  /** The team's tags on the shortlist, by name. */
  tags: string[];
  /** When the shortlist last changed (ISO), else when it was made. */
  updatedAt: string | null;
}

/** What the team's board knows of a shortlist besides what its client sees. */
export interface BoardShortlist extends ClientShortlist {
  id: number;
  workspaceId: number | null;
  teamId: number | null;
}

/**
 * What a card's QR code is made of: the custom field definition behind each part, and the unit
 * its measurements are written in. `null` unless the shortlist shows the QR code and its
 * workspace has it set up with the feature on.
 */
export interface ShortlistQrSetting {
  mapping: {
    gender: number | null;
    bust: number | null;
    waist: number | null;
    height: number | null;
    shoe: number | null;
    bra_size: number | null;
  };
  unit: 'cm' | 'in';
}

/** The shortlist without its blocks. */
export type ShortlistSummary = Shortlist & {
  board: BoardShortlist;
  blockCount: number;
  /** Votes and comments on the shortlist's people: the header's Feedback counter. */
  feedbackCount: number;
  /** Card fields shown on cards; the rest show in the details. */
  cardFieldCount: number;
  clientView: ShortlistClientView;
  qr: ShortlistQrSetting | null;
};

/** Who wrote a member's comment, as the server names them; guests sign with their own name. */
export interface CommentAuthor {
  name: string;
  pictureUrl: string | null;
}

/** A comment on a block; `author` is set for a team member, `null` for a guest. */
export type BoardComment = ShortlistAction & { author: CommentAuthor | null };

/** One card: a block with what its card shows. */
export interface ShortlistBlockCard {
  id: number;
  contactId: number;
  order: number | null;
  note: string | null;
  /** The contact cut to the shortlist's card fields. */
  contact: Resource | null;
  /** Lists cut to their first value for guests, with how many values they had. */
  listCounts: Record<string, number>;
  /**
   * The cover only, empty without pictures; `pinCount` is the whole gallery (`getBlockPins`).
   * A page asked with `previewPins` carries the first pictures in gallery order instead.
   */
  pins: Pin[];
  pinCount: number;
  /** `pins` are the gallery's first pictures, as a `previewPins` page brings them; absent for the cover alone. */
  pinsArePreview?: boolean;
  upvotes: ShortlistAction[];
  downvotes: ShortlistAction[];
  /** Oldest first (by time sent, then id): the order the server guarantees and the UI keeps. */
  comments: BoardComment[];
  upvoteCount: number;
  downvoteCount: number;
  commentCount: number;
  /** Bookings in the shortlist's availability dates; null when availability is off. */
  bookingEvents: Event[] | null;
}

/** Which blocks, in what order: the board's own search, filters and sort. */
export interface ShortlistBlockQuery {
  search: string;
  filters: ResourceSearchQuery['filters'];
  sort: { field: BlockSortField; direction: 'asc' | 'desc' };
  hasComments: boolean;
}

export interface ShortlistBlockPage {
  blocks: ShortlistBlockCard[];
  statuses: Status[];
  total: number;
}

export interface BrowsingScope {
  workspaceId: number | null;
  teamId: number | null;
}

export type AddContactsRequest = (
  | { type: 'contacts'; contactIds: number[] }
  | { type: 'search'; query: ResourceSearchQuery }
) & {
  /** Between these two blocks (null: the top or the bottom); without it, at the end. */
  position?: { beforeId: number | null; afterId: number | null };
};

export type RemoveBlocksRequest =
  | { type: 'blocks'; blockIds: number[] }
  | { type: 'search'; query: ShortlistBlockQuery };

export interface MoveBlocksRequest {
  blockIds: number[];
  beforeId: number | null;
  afterId: number | null;
}

/** A pin of a block's gallery as it is saved; `id` null for a new one. */
export interface BlockPinWrite {
  id: number | null;
  fileId: string | null;
  pinType: string;
  url: string;
}

export interface MoveBlocksResponse {
  orders: { id: number; order: number }[];
  rebalanced: boolean;
}

/** Which page a shortlist's workspace shows: the v5 one or today's. */
export type ShortlistVersion = 'v5' | 'legacy';

/** The team's public profile the client page is drawn with, by setting name, as stored. */
export type ShortlistBranding = Record<
  | 'public_company_name'
  | 'public_team_name'
  | 'public_brand_image'
  | 'public_theme'
  | 'defaults_locale',
  unknown
>;

/**
 * The client view's summary: a password shortlist before its password is only the gate (its
 * title and branding); once open, what a client's page needs that the app would otherwise read.
 */
export type ClientSummary =
  | {
      restricted: true;
      title: string | null;
      version: ShortlistVersion;
      branding: ShortlistBranding;
    }
  | {
      restricted: false;
      access: 'member' | 'guest';
      /**
       * Whether the client sees it: told to a member only (`null` for a guest, who only ever opens
       * a published, live one). An unpublished or archived one opens only to those who may edit it.
       */
      published: boolean | null;
      archived: boolean | null;
      title: string | null;
      version: ShortlistVersion;
      branding: ShortlistBranding;
      shortlist: ClientShortlist;
      /** Definitions of the custom fields among the card fields: a guest has no metastructure. */
      customFieldDefinitions: unknown[];
      /** The card fields' custom field types, for the client view's searches. */
      fieldMappings: FieldNameTypeMapping;
      pronouns: GlobalPronouns;
      qr: ShortlistQrSetting | null;
    };

/**
 * The shortlist board by its link, for members and guests alike (`/shortlist-boards/:publicId`):
 * reads in the view asked for, the board's own writes in Edit. The settings, a block's note and
 * its gallery are written through the shortlist's own endpoints, by its id.
 */
export interface ShortlistBoardApi {
  /** Edit's summary, by the shortlist's id or its link; a member's only. */
  getBoardSummary: (
    shortlist: number | string,
    scope: BrowsingScope | null
  ) => Promise<ShortlistSummary>;
  getClientSummary: (
    publicId: string,
    password: string | null
  ) => Promise<ClientSummary>;
  /**
   * Every block the query matches, in one response (no paging: a board holds a few hundred).
   * `fieldMappings`: the filter fields' types, as the view's reader knows them. `previewPins`:
   * the cards carry their first pictures, so a board that draws them needs no request per card.
   */
  getAllBlocks: (
    ref: BoardRef,
    fieldMappings: FieldNameTypeMapping,
    query: ShortlistBlockQuery,
    previewPins: boolean,
    signal?: AbortSignal
  ) => Promise<ShortlistBlockPage>;
  addContacts: (
    publicId: string,
    fieldMappings: FieldNameTypeMapping,
    request: AddContactsRequest
  ) => Promise<{ addedBlockIds: number[]; reactivatedBlockIds: number[] }>;
  removeBlocks: (
    publicId: string,
    fieldMappings: FieldNameTypeMapping,
    request: RemoveBlocksRequest
  ) => Promise<{ removedBlockIds: number[] }>;
  moveBlocks: (
    publicId: string,
    request: MoveBlocksRequest
  ) => Promise<MoveBlocksResponse>;

  /** A block's whole gallery in the view, in its order: the first is the cover. */
  getBlockPins: (ref: BoardRef, blockId: number) => Promise<Pin[]>;
  /** Writes the given shortlist columns (settings, title, note on a block go elsewhere). */
  updateShortlist: (
    id: number,
    patch: Partial<ShortlistSettingsPatch>
  ) => Promise<void>;
  updateBlockNote: (
    shortlistId: number,
    blockId: number,
    note: string
  ) => Promise<void>;
  /** Replaces a block's gallery in this order: pins left out are removed, pins without an id added. */
  updateBlockPins: (
    shortlistId: number,
    blockId: number,
    pins: BlockPinWrite[]
  ) => Promise<void>;
}

/** Shortlist columns the board edits, with their wire names. */
export interface ShortlistSettingsPatch {
  title: string | null;
  /** Whole tag rows: the server relates the listed tags and unrelates the rest. */
  tags: Tag[];
  public_title: string | null;
  public_description: string | null;
  default_fields: string[];
  card_field_count: number;
  /** The card size clients see; the server keeps it in v1's `layout`. */
  clientView: ShortlistClientView;
  show_availability: { start_date: string; end_date: string } | null;
  default_resources_query: ResourceSearchQuery | null;
  public: boolean;
  private_password: string | null;
  enable_voting: boolean;
  enable_commenting: boolean;
  public_feedback: boolean;
  enable_notifications: boolean;
  published: boolean;
  status: Shortlist['status'];
}

export const shortlistBlockQueryUtils = {
  ofDefault: (): ShortlistBlockQuery => ({
    search: '',
    filters: {},
    sort: { field: 'order', direction: 'asc' },
    hasComments: false
  }),
  /** A sort on `field` from its natural end: most votes and the newest first, names and the custom order from the top. */
  sortedBy: (field: BlockSortField): ShortlistBlockQuery['sort'] => ({
    field,
    direction: field === 'votes' || field === 'added' ? 'desc' : 'asc'
  })
};

export type ShortlistBoardErrorKind = 'belongs-elsewhere';

/**
 * The board's refusal of a request, by what it means: `belongs-elsewhere`, the shortlist lives in
 * another workspace or team than the one the request was made under.
 */
export class ShortlistBoardError extends Error {
  constructor(
    readonly kind: ShortlistBoardErrorKind,
    options?: ErrorOptions
  ) {
    super(`Shortlist board refused the request: ${kind}`, options);
    this.name = 'ShortlistBoardError';
  }
}
